import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  Check,
  ExternalLink,
  ImagePlus,
  Store,
  Trash2,
  User as UserIcon,
} from 'lucide-react';
import { ApiError, api } from '@/lib/api';
import { resizeForUpload } from '@/lib/image';
import { useAuth, useReference } from '@/hooks/useApp';
import { useSeo } from '@/hooks/useSeo';
import { SITE_NAME } from '@/lib/seo';
import { Button, Field, Input, Select, Spinner, Switch, Textarea } from '@/components/ui';
import { cn } from '@/lib/cn';
import { SellerType, isBusinessSeller } from '@/lib/types';
import type { UpdateProfileRequest, User } from '@/lib/types';

/**
 * What each type of seller actually is, in the seller's own words.
 *
 * Three cards rather than a dropdown: this is the one choice on the page that changes what the rest
 * of the form asks for, and a select box hides both the options and their consequences behind a
 * click. The wording matters as much as the control — "auto plac" and "auto salon" are the same
 * thing to a form and very different things to a buyer.
 */
const SELLER_TYPE_CARDS = [
  {
    value: SellerType.Private,
    label: 'Fizičko lice',
    description: 'Prodajete svoj automobil. Bez firme i poslovnih podataka.',
    Icon: UserIcon,
  },
  {
    value: SellerType.Dealer,
    label: 'Auto plac',
    description: 'Otkup i prodaja polovnih vozila na placu.',
    Icon: Store,
  },
  {
    value: SellerType.Showroom,
    label: 'Auto salon',
    description: 'Salon sa izložbenim prostorom, ovlašćeni ili nezavisni.',
    Icon: Building2,
  },
] as const;

function toForm(user: User): UpdateProfileRequest {
  return {
    sellerType: user.sellerType,
    displayName: user.displayName,
    bio: user.bio ?? '',
    cityId: user.cityId ?? null,
    contactPhone: user.contactPhone ?? '',
    showPhonePublicly: user.showPhonePublicly,
    dealershipName: user.dealershipName ?? '',
    businessAddress: user.businessAddress ?? '',
    publicEmail: user.publicEmail ?? '',
    websiteUrl: user.websiteUrl ?? '',
    businessHours: user.businessHours ?? '',
    taxNumber: user.taxNumber ?? '',
    companyNumber: user.companyNumber ?? '',
  };
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
}

/**
 * The seller's own profile.
 *
 * The page exists because every listing links to `/prodavac/:id` and, until a seller can say who
 * they are, that link lands on a name and nothing else. For an auto plac the public page is the
 * shop window — address, opening hours, phone — so this form is what fills it.
 */
export function ProfilePage() {
  const { user, applyProfile } = useAuth();
  const { data: reference } = useReference();

  useSeo({ title: `Moj profil | ${SITE_NAME}`, noIndex: true });

  const [form, setForm] = useState<UpdateProfileRequest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);

  const fileInput = useRef<HTMLInputElement>(null);

  // Seeded once per account. Re-seeding on every render of `user` would overwrite what the seller
  // is halfway through typing the moment anything else refreshes the session.
  const seeded = useRef<string | null>(null);

  useEffect(() => {
    if (user && seeded.current !== user.id) {
      seeded.current = user.id;
      setForm(toForm(user));
    }
  }, [user]);

  if (!user || !form) {
    return (
      <div className="flex justify-center py-24">
        <Spinner className="size-8 text-accent" />
      </div>
    );
  }

  const isBusiness = isBusinessSeller(form.sellerType);
  const errorFor = (field: string) => fieldErrors[field]?.[0];

  const update = <K extends keyof UpdateProfileRequest>(key: K, value: UpdateProfileRequest[K]) => {
    setSaved(false);
    setForm((current) => (current ? { ...current, [key]: value } : current));
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    setSaved(false);
    setBusy(true);

    try {
      const updated = await api<User>('/api/profile', { method: 'PUT', body: form });

      applyProfile(updated);
      setForm(toForm(updated));
      setSaved(true);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.message);
        setFieldErrors(caught.fieldErrors ?? {});
      } else {
        setError('Čuvanje profila nije uspelo. Pokušajte ponovo.');
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setBusy(false);
    }
  };

  const uploadPhoto = async (file: File | undefined) => {
    if (!file) return;

    setError(null);
    setPhotoBusy(true);

    try {
      // Resized in the browser for the same reason listing photos are: a logo dropped straight off
      // a design file is several megabytes of pixels nothing on the site will ever display.
      const resized = await resizeForUpload(file);

      const body = new FormData();
      body.append('photo', resized.thumbnail, `profile.${resized.contentType.split('/')[1]}`);

      applyProfile(await api<User>('/api/profile/photo', { method: 'POST', body }));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Slanje slike nije uspelo.');
    } finally {
      setPhotoBusy(false);

      // Cleared so selecting the same file again still fires a change event.
      if (fileInput.current) {
        fileInput.current.value = '';
      }
    }
  };

  const removePhoto = async () => {
    setPhotoBusy(true);

    try {
      applyProfile(await api<User>('/api/profile/photo', { method: 'DELETE' }));
    } catch {
      setError('Uklanjanje slike nije uspelo.');
    } finally {
      setPhotoBusy(false);
    }
  };

  const displayedName = (isBusiness && form.dealershipName) || form.displayName || user.displayName;

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-body">Vaš profil</h1>

        {/* The same link buyers follow from a listing, so the seller can check what they publish. */}
        <Link to={`/prodavac/${user.id}`} className="shrink-0">
          <Button variant="secondary" size="sm">
            <ExternalLink size={16} />
            Javni profil
          </Button>
        </Link>
      </div>

      {error && (
        <p
          role="alert"
          className="mb-5 rounded-xl border border-danger/20 bg-danger/10 p-4 text-sm text-danger"
        >
          {error}
        </p>
      )}

      {saved && (
        <p className="animate-fade-in-down mb-5 flex items-center gap-2 rounded-xl border border-success/20 bg-success/10 p-4 text-sm text-success">
          <Check size={16} />
          Profil je sačuvan.
        </p>
      )}

      <form onSubmit={submit} className="space-y-8">
        <section className="rounded-[var(--radius-panel)] border border-default bg-raised p-5 shadow-card">
          <h2 className="font-semibold text-body">Tip prodavca</h2>
          <p className="mt-1 mb-4 text-sm text-muted">
            Kupci vide ovu oznaku na svakom vašem oglasu.
          </p>

          <div className="grid gap-3 sm:grid-cols-3">
            {SELLER_TYPE_CARDS.map(({ value, label, description, Icon }) => {
              const selected = form.sellerType === value;

              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => update('sellerType', value)}
                  className={cn(
                    'flex flex-col gap-2 rounded-xl border p-4 text-left transition duration-200',
                    'hover:border-strong focus-visible:ring-4 focus-visible:ring-accent/20 focus-visible:outline-none',
                    selected
                      ? 'border-accent bg-accent-soft'
                      : 'border-default bg-sunken',
                  )}
                >
                  <span
                    className={cn(
                      'flex size-9 items-center justify-center rounded-lg',
                      selected ? 'bg-accent text-white' : 'bg-raised text-muted',
                    )}
                  >
                    <Icon size={18} />
                  </span>
                  <span
                    className={cn(
                      'text-sm font-semibold',
                      selected ? 'text-accent' : 'text-body',
                    )}
                  >
                    {label}
                  </span>
                  <span className="text-xs leading-snug text-faint">{description}</span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="rounded-[var(--radius-panel)] border border-default bg-raised p-5 shadow-card">
          <h2 className="mb-4 font-semibold text-body">
            {isBusiness ? 'Logo i naziv' : 'Slika i ime'}
          </h2>

          <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
            <div className="flex shrink-0 flex-col items-center gap-2">
              {user.profilePhotoUrl ? (
                <img
                  src={user.profilePhotoUrl}
                  alt=""
                  className="size-24 rounded-2xl border border-default object-cover"
                />
              ) : (
                <span className="flex size-24 items-center justify-center rounded-2xl bg-accent-soft font-display text-2xl font-bold text-accent">
                  {initials(displayedName)}
                </span>
              )}

              <div className="flex gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  loading={photoBusy}
                  onClick={() => fileInput.current?.click()}
                >
                  {!photoBusy && <ImagePlus size={14} />}
                  {user.profilePhotoUrl ? 'Promeni' : 'Dodaj'}
                </Button>

                {user.profilePhotoUrl && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={photoBusy}
                    onClick={removePhoto}
                    aria-label="Ukloni sliku"
                  >
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>

              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => uploadPhoto(event.target.files?.[0])}
              />
            </div>

            <div className="grid min-w-0 flex-1 gap-4">
              {isBusiness && (
                <Field
                  label="Naziv firme"
                  required
                  hint="Prikazuje se umesto vašeg imena na oglasima."
                  error={errorFor('dealershipName')}
                >
                  <Input
                    required
                    maxLength={120}
                    value={form.dealershipName ?? ''}
                    onChange={(event) => update('dealershipName', event.target.value)}
                  />
                </Field>
              )}

              <Field
                label="Ime i prezime"
                required
                hint={isBusiness ? 'Kontakt osoba. Ne prikazuje se kupcima.' : undefined}
                error={errorFor('displayName')}
              >
                <Input
                  required
                  maxLength={80}
                  value={form.displayName}
                  onChange={(event) => update('displayName', event.target.value)}
                />
              </Field>
            </div>
          </div>
        </section>

        <section className="rounded-[var(--radius-panel)] border border-default bg-raised p-5 shadow-card">
          <h2 className="mb-4 font-semibold text-body">Kontakt i lokacija</h2>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Telefon" required={isBusiness} error={errorFor('contactPhone')}>
              <Input
                type="tel"
                inputMode="tel"
                maxLength={30}
                placeholder="06x xxx xxxx"
                value={form.contactPhone ?? ''}
                onChange={(event) => update('contactPhone', event.target.value)}
              />
            </Field>

            <Field label="Grad" required={isBusiness} error={errorFor('cityId')}>
              <Select
                value={form.cityId ?? ''}
                onChange={(event) =>
                  update('cityId', event.target.value ? Number(event.target.value) : null)
                }
              >
                <option value="">Izaberite…</option>
                {reference?.cities.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name}
                  </option>
                ))}
              </Select>
            </Field>

            {isBusiness && (
              <>
                <Field
                  label="Adresa"
                  required
                  hint="Ulica i broj."
                  error={errorFor('businessAddress')}
                  className="sm:col-span-2"
                >
                  <Input
                    required
                    maxLength={200}
                    value={form.businessAddress ?? ''}
                    onChange={(event) => update('businessAddress', event.target.value)}
                  />
                </Field>

                <Field
                  label="E-mail za kontakt"
                  hint="Ne mora biti isti kao onaj za prijavu."
                  error={errorFor('publicEmail')}
                >
                  <Input
                    type="email"
                    maxLength={256}
                    value={form.publicEmail ?? ''}
                    onChange={(event) => update('publicEmail', event.target.value)}
                  />
                </Field>

                <Field
                  label="Veb-sajt"
                  hint="npr. www.mojplac.rs"
                  error={errorFor('websiteUrl')}
                >
                  <Input
                    maxLength={200}
                    value={form.websiteUrl ?? ''}
                    onChange={(event) => update('websiteUrl', event.target.value)}
                  />
                </Field>

                <Field
                  label="Radno vreme"
                  hint="npr. Pon–Pet 09–17, Sub 09–14"
                  error={errorFor('businessHours')}
                  className="sm:col-span-2"
                >
                  <Input
                    maxLength={200}
                    value={form.businessHours ?? ''}
                    onChange={(event) => update('businessHours', event.target.value)}
                  />
                </Field>
              </>
            )}
          </div>

          <div className="mt-2 border-t border-default pt-2">
            <Switch
              label="Prikaži telefon i neprijavljenim posetiocima"
              checked={form.showPhonePublicly}
              onChange={(checked) => update('showPhonePublicly', checked)}
            />
          </div>
        </section>

        {isBusiness && (
          <section className="animate-fade-in-down rounded-[var(--radius-panel)] border border-default bg-raised p-5 shadow-card">
            <h2 className="font-semibold text-body">Podaci o firmi</h2>
            <p className="mt-1 mb-4 text-sm text-muted">
              Nisu obavezni, ali oglasi sa unetim podacima o firmi deluju kupcima znatno
              pouzdanije.
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="PIB" hint="9 cifara" error={errorFor('taxNumber')}>
                <Input
                  inputMode="numeric"
                  maxLength={9}
                  value={form.taxNumber ?? ''}
                  onChange={(event) => update('taxNumber', event.target.value)}
                />
              </Field>

              <Field label="Matični broj" hint="8 cifara" error={errorFor('companyNumber')}>
                <Input
                  inputMode="numeric"
                  maxLength={8}
                  value={form.companyNumber ?? ''}
                  onChange={(event) => update('companyNumber', event.target.value)}
                />
              </Field>
            </div>
          </section>
        )}

        <section className="rounded-[var(--radius-panel)] border border-default bg-raised p-5 shadow-card">
          <h2 className="mb-4 font-semibold text-body">
            {isBusiness ? 'O firmi' : 'O vama'}
          </h2>

          <Field
            hint={
              isBusiness
                ? 'Čime se bavite, koliko dugo, šta nudite uz vozilo.'
                : 'Nekoliko rečenica o sebi. Prikazuje se na vašem profilu.'
            }
            error={errorFor('bio')}
          >
            <Textarea
              maxLength={1000}
              rows={5}
              value={form.bio ?? ''}
              onChange={(event) => update('bio', event.target.value)}
            />
          </Field>
        </section>

        <div className="flex justify-end gap-2">
          <Button type="submit" loading={busy}>
            Sačuvaj profil
          </Button>
        </div>
      </form>
    </div>
  );
}
